import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@shared/services/api";
import { ADMIN_CATEGORY_KEYS } from "@shared/constants/queryKeys";
import type {
  CreateCategoryData,
  UpdateCategoryData,
  Category,
} from "@admin/types/category";

type CategoryApiResponse = {
  success?: boolean;
  data?: Category;
  message?: string;
} & Partial<Category>;

const extractCategory = (response: CategoryApiResponse | Category): Category => {
  if ("data" in response && response.data) {
    return response.data;
  }
  return response as Category;
};

export function useCreateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCategoryData): Promise<Category> => {
      const response = await apiClient.post<CategoryApiResponse>("/category", data);
      return extractCategory(response);
    },
    onSuccess: (category) => {
      // Admin
      queryClient.invalidateQueries({ queryKey: ADMIN_CATEGORY_KEYS.all });

      // Público: categorías por catálogo, root y árbol
      if (category.catalogId) {
        queryClient.invalidateQueries({
          queryKey: ["categories", "catalog", category.catalogId],
        });
        queryClient.invalidateQueries({
          queryKey: ["categories", "root", "catalog", category.catalogId],
        });
        queryClient.invalidateQueries({
          queryKey: ["categoryTree", category.catalogId],
        });
      }

      // Público: subcategorías si tiene padre
      if (category.parentId) {
        queryClient.invalidateQueries({
          queryKey: ["categories", "subcategories", category.parentId],
        });
      }

      // Público: categorías destacadas
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "categories" &&
          query.queryKey[1] === "featured",
      });

      // Home / navbar
      queryClient.invalidateQueries({ queryKey: ["home", "data"] });
      queryClient.invalidateQueries({ queryKey: ["navbar"] });
    },
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateCategoryData): Promise<Category> => {
      const { id, ...updateData } = data;
      const response = await apiClient.put<CategoryApiResponse>(
        `/category/${id}`,
        updateData
      );
      return extractCategory(response);
    },
    onSuccess: (category) => {
      // Admin
      queryClient.invalidateQueries({ queryKey: ADMIN_CATEGORY_KEYS.all });

      // Público: categorías por catálogo, root y árbol
      if (category.catalogId) {
        queryClient.invalidateQueries({
          queryKey: ["categories", "catalog", category.catalogId],
        });
        queryClient.invalidateQueries({
          queryKey: ["categories", "root", "catalog", category.catalogId],
        });
        queryClient.invalidateQueries({
          queryKey: ["categoryTree", category.catalogId],
        });
      }

      // Público: subcategorías si tiene padre
      if (category.parentId) {
        queryClient.invalidateQueries({
          queryKey: ["categories", "subcategories", category.parentId],
        });
      }

      // Público: categorías destacadas
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "categories" &&
          query.queryKey[1] === "featured",
      });

      // Home / navbar
      queryClient.invalidateQueries({ queryKey: ["home", "data"] });
      queryClient.invalidateQueries({ queryKey: ["navbar"] });
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/category/${id}`);
    },
    onSuccess: () => {
      // Admin
      queryClient.invalidateQueries({ queryKey: ADMIN_CATEGORY_KEYS.all });

      // Público: invalidar todas las colecciones de categorías y árbol
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "categories",
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "categoryTree",
      });

      // Home / navbar
      queryClient.invalidateQueries({ queryKey: ["home", "data"] });
      queryClient.invalidateQueries({ queryKey: ["navbar"] });
    },
  });
}

export function useToggleCategoryStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      currentActive,
    }: {
      id: string;
      currentActive: boolean;
    }): Promise<Category> => {
      const endpoint = currentActive
        ? `/category/${id}/deactivate`
        : `/category/${id}/activate`;
      const response = await apiClient.patch<CategoryApiResponse>(endpoint);
      return extractCategory(response);
    },
    onSuccess: (category) => {
      // Admin
      queryClient.invalidateQueries({ queryKey: ADMIN_CATEGORY_KEYS.all });

      // Público: categorías por catálogo, root y árbol
      if (category.catalogId) {
        queryClient.invalidateQueries({
          queryKey: ["categories", "catalog", category.catalogId],
        });
        queryClient.invalidateQueries({
          queryKey: ["categories", "root", "catalog", category.catalogId],
        });
        queryClient.invalidateQueries({
          queryKey: ["categoryTree", category.catalogId],
        });
      }

      // Público: subcategorías si tiene padre
      if (category.parentId) {
        queryClient.invalidateQueries({
          queryKey: ["categories", "subcategories", category.parentId],
        });
      }

      // Público: categorías destacadas
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "categories" &&
          query.queryKey[1] === "featured",
      });

      // Home / navbar
      queryClient.invalidateQueries({ queryKey: ["home", "data"] });
      queryClient.invalidateQueries({ queryKey: ["navbar"] });
    },
  });
}

